/**
 * SOUND-847 — pure logic for the /alerts page (Disputifier alert coverage,
 * gaps and refund rate by merchant and MID).
 *
 * Everything that could be WRONG lives here, because sound-app has no
 * component-test harness (SOUND-679): the threshold → tone rule, "days ago"
 * from the data's own `asOf`, the stale-program rule, the filter-chip
 * predicates and the sort. The page only renders.
 *
 * Contract (docs/plans/2026-10-30-alerts-coverage-page.md, "Build contract → 3"):
 * rows carry server-computed `coverage` / `refundRate` (fractions 0..1, or null
 * when the denominator is 0) and `lastReceived` per program, which is ALL-TIME,
 * not bounded by the range. Dates are "YYYY-MM-DD" strings.
 */
import { parseSort } from "utilities/listSort";

/** Below this a rate is red. Fixed for V1 (CPO 2026-10-30). */
export const LOW_THRESHOLD = 0.85;
/** At or above this a rate is green; between the two it is amber. */
export const OK_THRESHOLD = 0.9;
/** A program (or a row's alerts overall) that last fired this many days before asOf is stale. */
export const STALE_DAYS = 7;

/** The three dispute-alert programs. TC40 is a fraud notice and is never in coverage or refund rate. */
export const ALERT_PROGRAMS = ["ethoca", "cdrn", "rdr"];
/** Every program the page shows a column for. */
export const PROGRAMS = ["ethoca", "cdrn", "rdr", "tc40"];
export const PROGRAM_LABELS = { ethoca: "Ethoca", cdrn: "CDRN", rdr: "RDR", tc40: "TC40" };

/** Threshold → semantic tone. null (no denominator) is muted, never green. */
export function rateTone(v) {
  if (v == null || !Number.isFinite(v)) return "muted";
  if (v < LOW_THRESHOLD) return "destructive";
  if (v < OK_THRESHOLD) return "warning";
  return "success";
}

/** 0.873 → "87.3%", 0.9 → "90%", null → "—". */
export function formatRate(v) {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(1).replace(/\.0$/, "")}%`;
}

const DAY_MS = 86400000;
function dayNumber(iso) {
  if (!iso || typeof iso !== "string") return null;
  const t = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(t) ? null : Math.round(t / DAY_MS);
}

/** Whole days from `date` to `asOf` (both "YYYY-MM-DD"). null if either is missing. */
export function daysAgo(date, asOf) {
  const d = dayNumber(date);
  const a = dayNumber(asOf);
  if (d == null || a == null) return null;
  return a - d;
}

/** "today" | "yesterday" | "Nd ago" | null. Relative to the DATA's asOf, not the wall clock. */
export function agoLabel(date, asOf) {
  const d = daysAgo(date, asOf);
  if (d == null) return null;
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  return `${d}d ago`;
}

/** A date at least STALE_DAYS before asOf. A program that never fired is NOT stale — it is absent. */
export function isStale(date, asOf) {
  const d = daysAgo(date, asOf);
  return d != null && d >= STALE_DAYS;
}

/** "YYYY-MM-DD" plus n days (n may be negative). */
export function addDays(iso, n) {
  const d = dayNumber(iso);
  if (d == null) return null;
  return new Date((d + n) * DAY_MS).toISOString().slice(0, 10);
}

/** A trailing N-day window ending on asOf, inclusive: 30 → asOf-29 .. asOf. */
export function presetRange(asOf, days) {
  if (!asOf) return { from: null, to: null };
  return { from: addDays(asOf, -(days - 1)), to: asOf };
}

/**
 * The latest Ethoca / CDRN / RDR date. Derived here rather than read from
 * `lastReceived.any`, because "silent" means no DISPUTE alert — whether the
 * API's `any` includes TC40 is not pinned by the contract.
 */
export function lastAlertDate(row) {
  const lr = row?.lastReceived || {};
  let best = null;
  for (const p of ALERT_PROGRAMS) {
    const v = lr[p];
    if (v && (!best || v > best)) best = v;
  }
  return best;
}

/** No Ethoca, CDRN or RDR for STALE_DAYS+ before asOf (and at least one ever). */
export function isSilent(row, asOf) {
  return isStale(lastAlertDate(row), asOf);
}

/** Programs (TC40 included) that fired at some point and then stopped for STALE_DAYS+. */
export function stoppedPrograms(row, asOf) {
  const lr = row?.lastReceived || {};
  return PROGRAMS.filter((p) => isStale(lr[p], asOf));
}

/** Row kind from the key prefix: m = merchant, d = MID, u = a merchant's records with no MID. */
export function rowKind(row) {
  const k = row?.key || "";
  return k.startsWith("m:") ? "merchant" : k.startsWith("u:") ? "unassigned" : "mid";
}

/** true / false for a MID row; null for merchant and no-MID rows (linking is not a question there). */
export function isLinked(row) {
  if (rowKind(row) !== "mid") return null;
  return Boolean(row.soundMidId || row.crm);
}

/** Display identity. CRM names win when the MID is linked; Disputifier's own naming otherwise. */
export function rowLabel(row) {
  const kind = rowKind(row);
  const merchant = row.disputifierMerchantName || row.disputifierMerchantId || "Unknown merchant";
  if (kind === "merchant") {
    const n = Number(row.midCount) || 0;
    return { title: merchant, subtitle: `${n} ${n === 1 ? "MID" : "MIDs"}` };
  }
  if (kind === "unassigned") {
    return { title: merchant, subtitle: "No MID on the record" };
  }
  const dName = row.disputifierMidName || row.disputifierMidId || "Unknown MID";
  const crm = row.crm;
  if (crm) {
    const title = `${crm.companyName || merchant} · ${crm.midNumber || dName}`;
    const parts = [crm.descriptor || row.descriptor, crm.processorName, merchant].filter(Boolean);
    return { title, subtitle: parts.join(" · ") };
  }
  return { title: dName, subtitle: [row.descriptor, merchant].filter(Boolean).join(" · ") };
}

/** Case-insensitive match across every name the row can be known by. */
export function matchesSearch(row, q) {
  const needle = (q || "").trim().toLowerCase();
  if (!needle) return true;
  const crm = row.crm || {};
  const hay = [
    row.disputifierMerchantName,
    row.disputifierMidName,
    row.disputifierMidId,
    row.descriptor,
    crm.companyName,
    crm.midNumber,
    crm.descriptor,
    crm.processorName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

/**
 * Filter chips. `grains` limits where a chip appears: "Not linked to CRM" is
 * only meaningful at MID grain. Predicates take (row, asOf).
 */
export const CHIPS = [
  {
    key: "coverage",
    label: "Coverage below 85%",
    grains: ["merchant", "mid"],
    test: (r) => r.coverage?.total != null && r.coverage.total < LOW_THRESHOLD,
  },
  {
    key: "refund",
    label: "Refund below 85%",
    grains: ["merchant", "mid"],
    test: (r) => r.refundRate != null && r.refundRate < LOW_THRESHOLD,
  },
  {
    key: "silent",
    label: "No alerts in 7 days",
    grains: ["merchant", "mid"],
    test: (r, asOf) => isSilent(r, asOf),
  },
  {
    key: "stopped",
    label: "A program stopped",
    grains: ["merchant", "mid"],
    test: (r, asOf) => stoppedPrograms(r, asOf).length > 0,
  },
  {
    key: "unlinked",
    label: "Not linked to CRM",
    grains: ["mid"],
    test: (r) => isLinked(r) === false,
  },
];

export function chipsFor(grain) {
  return CHIPS.filter((c) => c.grains.includes(grain));
}

/** Rows passing EVERY active chip (AND). Chips not offered at this grain are ignored. */
export function applyChips(rows, active, asOf, grain) {
  const on = chipsFor(grain).filter((c) => active?.has?.(c.key));
  if (!on.length) return rows;
  return rows.filter((r) => on.every((c) => c.test(r, asOf)));
}

/** { [chipKey]: count } over the unfiltered rows, so each chip says what it would select. */
export function chipCounts(rows, asOf, grain) {
  const out = {};
  for (const c of chipsFor(grain)) out[c.key] = rows.filter((r) => c.test(r, asOf)).length;
  return out;
}

/**
 * Sort values. Program columns sort by LAST RECEIVED, which is the question
 * the column answers ("when did this stop?"), not by in-range count.
 */
const SORT_VALUE = {
  name: (r) => rowLabel(r).title.toLowerCase(),
  coverage: (r) => r.coverage?.total ?? null,
  ethoca: (r) => r.lastReceived?.ethoca || null,
  cdrn: (r) => r.lastReceived?.cdrn || null,
  rdr: (r) => r.lastReceived?.rdr || null,
  tc40: (r) => r.lastReceived?.tc40 || null,
  chargebacks: (r) => r.counts?.chargebacks ?? 0,
  refundRate: (r) => r.refundRate ?? null,
  lastAlert: (r) => lastAlertDate(r),
  alerts: (r) => r.counts?.alerts ?? 0,
};

/**
 * Sort by a `"-field"` string (the RecordTable convention). Nulls — no
 * denominator, never received — sort LAST in both directions: a row with no
 * coverage figure is not the worst coverage, it is not a coverage question.
 * Ties fall back to name so the order is stable.
 */
export function sortRows(rows, sort) {
  const { field, dir } = parseSort(sort);
  const get = SORT_VALUE[field];
  if (!get) return rows.slice();
  const sign = dir === "desc" ? -1 : 1;
  const name = SORT_VALUE.name;
  return rows.slice().sort((x, y) => {
    const a = get(x);
    const b = get(y);
    if (a == null && b == null) return name(x) < name(y) ? -1 : name(x) > name(y) ? 1 : 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (a < b) return -sign;
    if (a > b) return sign;
    return name(x) < name(y) ? -1 : name(x) > name(y) ? 1 : 0;
  });
}

/**
 * Refund outcomes for the modal. "Other" is the remainder (declined, overlap,
 * threshold) and is floored at 0 so a data quirk never renders negative.
 */
export function refundOutcomes(counts = {}) {
  const alerts = counts.alerts || 0;
  const refunded = counts.refunded || 0;
  const alreadyRefunded = counts.alreadyRefunded || 0;
  const orderNotFound = counts.orderNotFound || 0;
  const other = Math.max(0, alerts - refunded - alreadyRefunded - orderNotFound);
  return { alerts, refunded, alreadyRefunded, orderNotFound, other };
}

/** More than 5% of alerts couldn't find the order → check the CRM/gateway connection. */
export function orderNotFoundWarning(counts = {}) {
  const alerts = counts.alerts || 0;
  return alerts > 0 && (counts.orderNotFound || 0) / alerts > 0.05;
}

/** Chargebacks per day split into matched-to-an-alert and no-alert, for the stacked chart. */
export function chargebackDays(days = []) {
  return days.map((d) => {
    const total = d.chargebacks || 0;
    const unmatched = Math.min(total, d.chargebacksUnmatched || 0);
    return { date: d.date, unmatched, matched: total - unmatched };
  });
}

/** KPI: how many rows sit below the coverage threshold. */
export function countBelow(rows) {
  return rows.filter((r) => r.coverage?.total != null && r.coverage.total < LOW_THRESHOLD).length;
}

/** "2026-10-03" → "Oct 3". Calendar date, rendered in UTC so no timezone shifts it a day. */
export function formatDay(iso) {
  const d = dayNumber(iso);
  if (d == null) return "—";
  return new Date(d * DAY_MS).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Classify a failed /alerts load. A 503 means the mart has not reached
 * presentation PG yet — an expected state in a fresh environment, shown as an
 * empty state, never as an error. Anything else is a real failure.
 */
export function alertLoadState(error) {
  if (!error) return "ok";
  return error?.response?.status === 503 ? "not-synced" : "error";
}

/**
 * Card brands with their own coverage figure (CPO 2026-10-31). There is NO
 * "other" any more: it read ~14% everywhere because ~1,500 chargebacks per 30
 * days carry no card brand at all, not because Amex/Discover cover badly.
 * Unknown-brand alerts and chargebacks still count toward `coverage.total`;
 * they just get no per-brand figure.
 */
//
// `graded`: only Visa and Mastercard are held to the 85/90 tones (CPO
// 2026-10-31, option A). The alert programs are mostly Visa/MC — RDR and CDRN
// are Visa-only, Ethoca mostly MC — so Amex/Discover coverage sits around
// 20–30% STRUCTURALLY. Red there would be permanent noise, so they render in a
// neutral tone.
export const BRANDS = [
  { key: "visa", label: "Visa", short: "V", graded: true },
  { key: "mastercard", label: "Mastercard", short: "MC", graded: true },
  { key: "amex", label: "Amex", short: "AX", graded: false },
  { key: "discover", label: "Discover", short: "DS", graded: false },
];

const BRAND_BY_KEY = Object.fromEntries(BRANDS.map((b) => [b.key, b]));

/**
 * Tone for a coverage figure, by brand. "total" (or no brand), Visa and
 * Mastercard get the 85/90 thresholds; Amex and Discover are always neutral
 * ("muted"), whatever their value.
 */
export function brandTone(brandKey, value) {
  const brand = BRAND_BY_KEY[brandKey];
  if (brand && !brand.graded) return "muted";
  return rateTone(value);
}

/**
 * The table's brand line: only brands with a coverage figure. `low` (red) is
 * set only for graded brands below 85%.
 */
export function brandBreakout(coverage) {
  return BRANDS.map((b) => ({ ...b, value: coverage?.[b.key] ?? null }))
    .filter((b) => b.value != null && Number.isFinite(b.value))
    .map((b) => ({ ...b, low: brandTone(b.key, b.value) === "destructive" }));
}

/** Modal helper copy beside the ungraded (Amex/Discover) tiles. */
export const UNGRADED_BRAND_NOTE =
  "Alert programs mostly cover Visa and Mastercard, so Amex and Discover coverage runs low.";

/** Hint under a brand stat tile. A null coverage means the brand had no alerts and no unmatched chargebacks. */
export function brandTileHint(counts, coverageValue) {
  if (coverageValue == null) return "no activity";
  const n = (v) => new Intl.NumberFormat("en-US").format(Number(v || 0));
  return `${n(counts?.alerts)} alerts · ${n(counts?.unmatched)} unmatched`;
}

/** The modal note for chargebacks with no card brand, or null when there are none. */
export function unknownBrandNote(byBrand) {
  const n = Number(byBrand?.unknown?.unmatched) || 0;
  if (n <= 0) return null;
  const num = new Intl.NumberFormat("en-US").format(n);
  return n === 1
    ? "1 chargeback has no card brand. It counts toward total coverage only."
    : `${num} chargebacks have no card brand. They count toward total coverage only.`;
}
